import React, { useLayoutEffect } from "react";
// import { useTranslation } from "react-i18next";
import SplitType from "split-type";
import gsap from "gsap";
import OtherProjects from "../../components/Other-Projects/otherProjects";
import ProjectNav from "../../components/Project-Nav/projectNav";

export default function ExchangeProject() {
  // const { t } = useTranslation();

  useLayoutEffect(() => {
    const text = new SplitType(".words1", { types: "chars" });
    const textA = SplitType.create(".sentence1", { types: "words" });
    const textB = SplitType.create(".line1", { types: "lines" });

    gsap.fromTo(
      text.chars,
      { y: 0, opacity: 0 },
      { y: -12, opacity: 1, stagger: 0.03 }
    );
    gsap.fromTo(
      textA.words,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.06 }
    );
    gsap.fromTo(
      textB.lines,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.1 }
    );

    gsap.fromTo(
      ".box1",
      { y: 0, opacity: 0, delay: 1 },
      { y: -20, opacity: 2, duration: 1 }
    );

    gsap.config({
      force3D: false,
      nullTargetWarn: false,
      trialWarn: false,
    });
  });

  return (
    <div className="project-section case-study exchange-project container mt-120">
      <div className="project-hero-card bg-secondary radius-20 w-100 p-40 sm:p-20 xs:p-20 box1">
        <div className="project-hero-title mb-20 sm:mb-12 xs:mb-8">
          <h1 className="f-40 xs:f-18 xs:l-h-24 sm:f-32 l-h-40 f-w-600 text-primary words1">
            CoinExchange
          </h1>
          <p className="f-16 xs:f-12 xs:l-h-20 sm:f-14 l-h-30 f-w-300 text-primary mt-8 sentence1">
            <span>Fintech · Crypto Exchange · Desktop + Mobile · Calibraint Technologies</span>
          </p>
        </div>
      </div>

      <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mt-24 mb-20 mw-680 line1">
        A multi-blockchain crypto exchange for desktop, iOS, and Android. My
        job was to design the spot trading interface, portfolio views,
        order book, and the full component library — in both light and
        dark themes. One of the more technically demanding projects I'd
        worked on at that point, because real-time trading data changes how
        you have to think about design states entirely.
      </p>

      <div className="cs-pills">
        <span className="cs-pill">Crypto / Fintech</span>
        <span className="cs-pill">Desktop + Mobile</span>
        <span className="cs-pill">Light &amp; Dark Themes</span>
        <span className="cs-pill">Calibraint · 6 months</span>
      </div>

      <div className="cs-stats">
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#4ade80" }}>2</div>
          <div className="cs-stat-l">Theme variants</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#38bdf8" }}>3</div>
          <div className="cs-stat-l">Platforms — web, iOS, Android</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#fb923c" }}>6mo</div>
          <div className="cs-stat-l">Duration</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#a78bfa" }}>✓</div>
          <div className="cs-stat-l">Alpha launched</div>
        </div>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">The product</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          CoinExchange is a multi-blockchain exchange for trading, buying,
          and selling crypto on desktop, iOS, and Android. The challenge
          with exchange design is that your users go from someone making
          their first ever purchase to a trader who's been at it for years.
          Both need to succeed in the same interface without feeling like
          they're in the wrong product.
        </p>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">My role</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          UI/UX designer — led user research, built the journey map,
          designed the full component library, and produced all
          high-fidelity screens across the trading, portfolio, wallet, and
          market overview sections. Also involved in the developer handoff
          for both web and mobile.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Research</div>
        <h3 className="f-20 l-h-28 f-w-600 text-primary mb-12 sentence1">
          Studying Binance, Coinbase, and Kraken
        </h3>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mb-12 line1">
          Went through the three dominant exchanges in detail — mapped
          their trading screens, order books, chart interactions, and
          onboarding flows. Created a journey map covering the full user
          lifecycle from account creation through to advanced order types.
          The goal was to understand what patterns experienced traders
          already relied on, so we didn't break those habits unnecessarily.
        </p>
        <div className="cs-quote">
          <p>
            "Binance has everything I need but I couldn't explain it to
            someone else if I tried. It's powerful but only if you already
            know what you're looking at."
          </p>
          <span>— Trader interview</span>
        </div>
      </div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 exchange-img1"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 exchange-flow"></div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">What came up in research</div>
        <div className="cs-pains">
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Trading screens showed 40+ data points simultaneously with no
              clear visual hierarchy — overwhelming for anyone who wasn't
              already a power user
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Light/dark theme inconsistency on competitor apps — elements
              clearly designed for dark looked broken in light mode and
              vice versa
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Order placement had too many confirmation steps — a real
              problem for experienced traders during fast-moving markets
              where speed matters
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Mobile trading was just a scaled-down desktop layout —
              critical actions buried in nested menus on smaller screens
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Portfolio performance numbers had no market context — a
              number by itself doesn't tell you if you should be worried
              or not
            </div>
          </div>
        </div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-680 sm:minh-450 xs:minh-200 mt-32 box1 exchange-img2"></div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Design decisions</div>
        <div className="cs-two-col">
          <div className="cs-card box1">
            <h4>Colour as the primary language</h4>
            <p>
              Green for gains, red for losses, amber for pending — applied
              consistently everywhere. Users read market state through
              colour before they read any number. Had to be rock-solid
              across both themes.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Dark first, light as an equal</h4>
            <p>
              Designed dark theme first — the standard for trading
              globally. Built light as a full peer, not an afterthought.
              Every component exists in both states with no corners cut.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Components before screens</h4>
            <p>
              Built the trading component library first — chart container,
              order book panel, trade form, portfolio card — before any
              full screens. Kept the design consistent and the handoff much
              cleaner.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Mobile from scratch</h4>
            <p>
              Mobile screens weren't adapted from desktop — designed fresh
              for smaller viewports. Key actions in thumb-reach zones.
              Advanced features one level deeper.
            </p>
          </div>
        </div>
      </div>

      <div className="cs-img bg-white radius-20 minh-680 sm:minh-450 xs:minh-200 mt-32 box1 exchange-img3"></div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 exchange-img4"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 exchange-img5"></div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-680 sm:minh-450 xs:minh-200 mt-32 box1 exchange-img6"></div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Outcomes</div>
        <div className="cs-outcomes">
          <div className="cs-outcome">
            <div className="ico">🎨</div>
            <h4>Full design system</h4>
            <p>
              Complete component library across both themes — every state,
              every variant, ready for dev.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🌓</div>
            <h4>Theme parity</h4>
            <p>
              Both themes with identical feature coverage — nothing in one
              that's missing in the other.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">📱</div>
            <h4>3 platforms</h4>
            <p>
              Web, iOS, and Android — mobile designed natively, not adapted
              from desktop.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🚀</div>
            <h4>Alpha launched</h4>
            <p>
              Design built into an internal alpha and reviewed by the full
              team before wider release.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🗺️</div>
            <h4>Journey map done</h4>
            <p>
              Full lifecycle from first signup through advanced trading —
              guided feature priority and screen sequencing.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">📊</div>
            <h4>Order book component</h4>
            <p>
              Real-time order book with live bid/ask data and visual
              density controls for different experience levels.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-32">
        <div className="cs-lbl">Reflection</div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Real-time data changes how you design.</strong> Static
            mockups don't capture what a trading interface feels like when
            numbers update every second. I had to design for state
            transitions — what does a price change look like mid-animation,
            not just before and after.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>The component library is the real deliverable.</strong>{" "}
            Individual screens become outdated within a sprint. A
            well-built component library stays useful for years. This
            project made me understand that systems-level design has more
            leverage than screen-level design.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Design both themes simultaneously.</strong> Building
            dark first and adapting to light always produces a weaker
            secondary theme. Every decision needs to be made in both
            contexts at the same time.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>If I did this again:</strong> More testing with
            first-time crypto buyers. Most research skewed toward
            experienced traders, and the new-user onboarding could have
            been significantly more progressive based on what we saw later.
          </p>
        </div>
      </div>

      <div className="project-bottom-nav mt-60 mb-100 flex justify-between align-center box1">
        <div className="flex xs:justify-center">
          <OtherProjects active="exchange" />
        </div>
        <ProjectNav current="exchange" />
      </div>
    </div>
  );
}
